import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../axiosInstance";

const BASE_URL = "/api/v1/seller-products";

// 1. Get all products (Admin)
export const getAllProducts = createAsyncThunk(
  "products/getAll",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}?page=${page}&limit=${limit}`);
      return res.data; // {success, page, totalPages, totalProducts, data}
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 2. Get product by Category ID
export const getProductsByCategoryId = createAsyncThunk(
  "products/getByCategory",
  async (categoryId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/${categoryId}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 3. Get product by ID
export const getProductById = createAsyncThunk(
  "products/getById",
  async (productId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/get/product/${productId}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 4. Add Product
export const addProduct = createAsyncThunk(
  "products/add",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.product || res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 5. Update Product
export const updateProduct = createAsyncThunk(
  "products/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE_URL}/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.updatedProduct || res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 6. Delete Product
export const deleteProduct = createAsyncThunk(
  "products/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 7. Get Seller’s Own Products
export const getUserProducts = createAsyncThunk(
  "products/getByUser",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/get/user/product?page=${page}&limit=${limit}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 8. Get Buyer View Products
export const getBuyerProducts = createAsyncThunk(
  "products/getBuyerProducts",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/get/buyer/products`);
      return res.data.products;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 9. Get Products by Buyer Category
export const getProductsByBuyerCategoryId = createAsyncThunk(
  "products/getByBuyerCategory",
  async ({ buyerCategoryId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/get/${buyerCategoryId}?page=${page}&limit=${limit}`
      );
      return res.data;
     
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 10. Update Product Buyer Category Visibility
export const updateProductBuyerCategoryVisibility = createAsyncThunk(
  "products/updateBuyerCategoryVisibility",
  async ({ productId, buyerCategory, visible, price }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${BASE_URL}/update-buyer-category-visibility`,
        {
          productId,
          buyerCategory,
          visible,
          price,
        }
      );
      return res.data.product; // updated product
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// // 11. Get Products by Connection (for Buyer)
// export const getProductsByConnection = createAsyncThunk(
//   "products/getProductsByConnection",
//   async ({ connectionId, otherUserId }, { rejectWithValue }) => {
//     try {
//       const res = await axios.post(`${BASE_URL}/get/products-by-connection`, {
//         connectionId,
//         otherUserId,
//       });
//       return res.data;
//     } catch (err) {
//       return rejectWithValue(err.response?.data);
//     }
//   }
// );

// 11. Get Products by Connection (for Buyer) - UPDATED WITH PAGINATION
export const getProductsByConnection = createAsyncThunk(
  "products/getProductsByConnection",
  async ({ connectionId, otherUserId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/get/products-by-connection?page=${page}&limit=${limit}`, 
        {
          connectionId,
          otherUserId,
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 12. Hide products for a buyerCategory
export const hideProductsByBuyerCategory = createAsyncThunk(
  "products/hideByBuyerCategory",
  async ({ buyerCategoryId, productCategoryId }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/hide-products-by-buyer-category`,
        { buyerCategoryId, productCategoryId }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// 13. apply Global Discount
export const applyGlobalDiscount = createAsyncThunk(
  "products/applyGlobalDiscount", // Changed from "product/applyGlobalDiscount"
  async (payload, { rejectWithValue }) => { // Remove the extra curly braces
    try {
      const res = await axios.post(
        `${BASE_URL}/apply-global-discount`, 
        payload // Send payload directly, not wrapped in object
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);


const productSlice = createSlice({
  name: "products",
  initialState: {
    allProducts: [],
    userProducts: [],
    buyerProducts: [],
    productsByBuyerCategory: [],
    productsByConnection: [], // Add new state for connection products
    buyerCategory: null,
    page: 1,
    totalPages: 1,
    totalProducts: 0,

    // Connection-specific pagination states
    connectionPage: 1,
    connectionTotalPages: 1,
    connectionTotalProducts: 0,

     // Global discount state - PROPERLY DEFINED
    globalDiscount: {
      loading: false,
      success: false,
      error: null,
      data: null
    },

    productDetails: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearProductDetails: (state) => {
      state.productDetails = null;
    },
    clearConnectionProducts: (state) => {
      state.productsByConnection = [];
      state.connectionPage = 1;
      state.connectionTotalPages = 1;
      state.connectionTotalProducts = 0;
      state.buyerCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ------------------------------ Get All ------------------------------ */
      .addCase(getAllProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.allProducts = action.payload.data;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalProducts = action.payload.totalProducts;
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      /* ------------------------- Get User Products ------------------------- */
      .addCase(getUserProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.userProducts = action.payload.product;
        state.page = action.payload.pagination.currentPage;
        state.totalPages = action.payload.pagination.totalPages;
        state.totalProducts = action.payload.pagination.totalProducts;
      })

      .addCase(getUserProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      /* ----------------------- Get Buyer View Products ---------------------- */
      .addCase(getBuyerProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBuyerProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.buyerProducts = action.payload;
      })
      .addCase(getBuyerProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      /* -------------------------- Buyer Category --------------------------- */
      .addCase(getProductsByBuyerCategoryId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProductsByBuyerCategoryId.fulfilled, (state, action) => {
        state.loading = false;
        state.productsByBuyerCategory = action.payload.products || [];
        state.buyerCategory = action.payload.buyerCategory;
        state.productCategoryPage = action.payload.pagination?.currentPage || 1;
        state.productCategoryTotalPages = action.payload.pagination?.totalPages || 1;
        state.productCategoryTotalProducts = action.payload.pagination?.totalProducts || 0;
      })
      .addCase(getProductsByBuyerCategoryId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch products by buyer category";
      })

      /* ---------------------------- Add Product ---------------------------- */
      .addCase(addProduct.fulfilled, (state, action) => {
        state.allProducts.push(action.payload);
      })

      /* --------------------------- Update Product -------------------------- */
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.allProducts.findIndex(
          (p) => p._id === action.payload._id
        );
        if (index !== -1) {
          state.allProducts[index] = action.payload;
        }
      })

      /* --------------------------- Delete Product -------------------------- */
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.allProducts = state.allProducts.filter(
          (p) => p._id !== action.payload
        );
      })
      // ------------------ Update Product Buyer Category Visibility ------------------
      .addCase(updateProductBuyerCategoryVisibility.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        updateProductBuyerCategoryVisibility.fulfilled,
        (state, action) => {
          state.loading = false;
          const updatedProduct = action.payload;
          const index = state.allProducts.findIndex(
            (p) => p._id === updatedProduct._id
          );
          if (index !== -1) {
            state.allProducts[index] = updatedProduct;
          }
          // Also update userProducts if needed
          const userIndex = state.userProducts.findIndex(
            (p) => p._id === updatedProduct._id
          );
          if (userIndex !== -1) {
            state.userProducts[userIndex] = updatedProduct;
          }
        }
      )
      .addCase(
        updateProductBuyerCategoryVisibility.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload?.message || "Failed to update product visibility";
        }
      )
      // ---------------------- Get Products by Connection ----------------------
      // .addCase(getProductsByConnection.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      // .addCase(getProductsByConnection.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.allProducts = action.payload.data || [];
      //   state.buyerCategory = action.payload.buyerCategory || null;
      // })
      // .addCase(getProductsByConnection.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error =
      //     action.payload?.message || "Failed to fetch products by connection";
      // })

      .addCase(getProductsByConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductsByConnection.fulfilled, (state, action) => {
        state.loading = false;
        state.productsByConnection = action.payload.data || [];
        state.buyerCategory = action.payload.buyerCategory || null;
        
        // Set pagination info from API response
        if (action.payload.pagination) {
          state.connectionPage = action.payload.pagination.currentPage;
          state.connectionTotalPages = action.payload.pagination.totalPages;
          state.connectionTotalProducts = action.payload.pagination.totalProducts;
        }
      })
      .addCase(getProductsByConnection.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch products by connection";
      })

      // ---------------------- Hide Products By Buyer Category ----------------------
      .addCase(hideProductsByBuyerCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hideProductsByBuyerCategory.fulfilled, (state, action) => {
        state.loading = false;
        // Update the products in allProducts to reflect visibility=false for this buyerCategory
        const buyerCategoryId = action.meta.arg;

        state.allProducts = state?.allProducts?.map((product) => {
          const updatedVisibility = product?.productVisibility?.map((v) => {
            if (v.buyerCategory === buyerCategoryId) {
              return { ...v, visible: false };
            }
            return v;
          });
          return { ...product, productVisibility: updatedVisibility };
        });

        // Similarly update userProducts if needed
        state.userProducts = state.userProducts.map((product) => {
          const updatedVisibility = product.productVisibility.map((v) => {
            if (v.buyerCategory === buyerCategoryId) {
              return { ...v, visible: false };
            }
            return v;
          });
          return { ...product, productVisibility: updatedVisibility };
        });
      })
      .addCase(hideProductsByBuyerCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to hide products by buyerCategory";
      })

      .addCase(applyGlobalDiscount.pending, (state) => {
        state.globalDiscount.loading = true;
        state.globalDiscount.success = false;
        state.globalDiscount.error = null;
      })
      .addCase(applyGlobalDiscount.fulfilled, (state, action) => {
        state.globalDiscount.loading = false;
        state.globalDiscount.success = true;
        state.globalDiscount.data = action.payload;
      })
      .addCase(applyGlobalDiscount.rejected, (state, action) => {
        state.globalDiscount.loading = false;
        state.globalDiscount.success = false;
        state.globalDiscount.error = action.payload?.message || "Failed to apply bulk pricing";
      })
  },
});

export const { clearProductDetails } = productSlice.actions;
export default productSlice.reducer;
